import { Injectable, signal, computed } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { ColumnData, UpdateColumnRequest, UpdateColumnResponse } from '../types/ColumnData';
import { SelectColumnsComponent } from '../shared-components/select-columns/select-columns.component';
import { SnackService } from './snack.service';
import { AuthorizationService } from './authorization.service';

export interface ColumnSaveConfig {
    tableName: string;
    saveMethod: (req: UpdateColumnRequest) => Observable<any> | null;
}

@Injectable()
export class ColumnManagerService {
    readonly columnData = signal<ColumnData[]>([]);
    readonly savedColumnData = signal<ColumnData[]>([]);
    readonly dirty = signal(false);

    readonly frozenData = computed(() =>
        this.columnData().filter(e => e.freeze)
    );

    readonly warmData = computed(() =>
        this.columnData().filter(e => !e.freeze)
    );

    readonly displayedColumns = computed(() => {
        const frozen = this.frozenData();
        const warm = this.warmData().filter(e => e.visible);
        return [...frozen, ...warm].map(e => e.id);
    });

    initColumns(userResult: UpdateColumnResponse): void {
        if (userResult.ColumnDataCustom != null) {
            userResult.ColumnDataCustom = userResult.ColumnDataCustom.sort((a, b) => a.index - b.index);
            this.columnData.set(userResult.ColumnDataCustom);
        } else {
            userResult.ColumnDataDefault = userResult.ColumnDataDefault.sort((a, b) => a.index - b.index);
            this.columnData.set(userResult.ColumnDataDefault);
        }
        this.savedColumnData.set(JSON.parse(JSON.stringify(userResult.ColumnDataDefault)));
    }

    getFreeze(columnid: string): boolean {
        const col = this.columnData().filter(e => e.id == columnid);
        if (col.length != 0) {
            return col[0].freeze;
        }
        return false;
    }

    save(config: ColumnSaveConfig, auth: AuthorizationService, snackMsg: SnackService): void {
        snackMsg.openSnackBar("Saving your changes, please wait!", 5000);
        const req = new UpdateColumnRequest(config.tableName);
        req.userid = auth.getCrmID();
        req.column_data = this.columnData();
        const obs = config.saveMethod(req);
        if (obs == null) {
            snackMsg.openSnackBar("An error occurred while saving your column settings.  Please try again later.", 5000);
            return;
        }
        obs.subscribe({
            next: (respond) => {
                if ((respond.TotalUpdated > 0) || (respond.number_updated > 0)) {
                    snackMsg.openSnackBar("Your column settings have been saved.", 5000);
                } else {
                    snackMsg.openSnackBar("An error occurred while saving your column settings.  Please try again later.", 5000);
                }
            },
            error: () => {
                snackMsg.openSnackBar("An error occurred while saving your column settings.  Please try again later.", 5000);
            }
        });
    }

    openSelectColumnsDialog(
        dialog: MatDialog,
        dataSource: MatTableDataSource<any>,
        applyFilterFn: () => void,
        saveConfig: ColumnSaveConfig,
        snackMsg: SnackService,
        auth: AuthorizationService
    ): void {
        const dialogRef = dialog.open(SelectColumnsComponent, {
            data: {
                datasource: dataSource,
                column_data: JSON.parse(JSON.stringify(this.columnData())),
                saved_column_data: JSON.parse(JSON.stringify(this.savedColumnData())),
                dirty: this.dirty()
            },
            width: '500px',
            panelClass: 'selectcolumns-dialog'
        });
        dialogRef.componentInstance.emitService.subscribe((val) => {
            this.columnData.set(val);
            applyFilterFn();
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.dirty != null) {
                this.dirty.set(result.dirty);
            }
            if (result && result.save) {
                if (this.dirty()) {
                    this.dirty.set(false);
                    this.save(saveConfig, auth, snackMsg);
                } else {
                    snackMsg.openSnackBar("Nothing to save.", 5000);
                }
            }
        });
    }
}
